import React, { useState, useEffect, useRef } from "react";
import styles from "./jumpingorbits.module.css";
import "bootstrap/dist/css/bootstrap.min.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import { useAudio } from "../../../Providers/AudioProvider";
import task from "../Task";

const JumpingOrbits = () => {
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [score, setScore] = useState(0);
  const [depth, setDepth] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentOrbit, setCurrentOrbit] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [isMobile, setIsMobile] = useState(true);
  const [showMobileControls, setShowMobileControls] = useState(true);
  const gameBoardRef = useRef(null);
  const gameAreaRef = useRef(null);
  const playerRef = useRef(null);

  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit";
  const playClick = useAudio();

  // Game constants
  const ORBIT_COUNT = 5;
  const ORBIT_SPACING = 60;
  const PLAYER_SIZE = 30;
  const OBSTACLE_SIZE = 20;
  const OBSTACLE_SPEED = 3;
  const OBSTACLE_SPAWN_RATE = 0.02;
  const DEPTH_INCREMENT = 0.1;
  const SCORE_MULTIPLIER = 10;

  // Initialize game
  const initializeGame = () => {
    setGameOver(false);
    setGameWon(false);
    setScore(0);
    setDepth(0);
    setCurrentOrbit(2); // Start in middle orbit
    setObstacles([]);
    setGameStarted(true);
  };

  // Handle player jump
  const handleJump = (direction) => {
    if (!gameStarted || gameOver || gameWon) return;

    const newOrbit = currentOrbit + direction;
    if (newOrbit >= 0 && newOrbit < ORBIT_COUNT) {
      setCurrentOrbit(newOrbit);
    }
  };

  // Handle touch/click events
  const handleTouchStart = (e) => {
    if (!gameStarted || gameOver || gameWon) return;
    e.preventDefault();

    const touch = e.touches[0];
    const gameArea = gameAreaRef.current;
    if (!gameArea) return;

    const touchX = touch.clientX;
    const centerX = gameArea.offsetWidth / 2;

    // Jump left or right based on touch position
    handleJump(touchX < centerX ? -1 : 1);
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (!gameStarted || gameOver || gameWon) return;

    if (e.key === "ArrowLeft") {
      handleJump(-1);
    } else if (e.key === "ArrowRight") {
      handleJump(1);
    }
  };

  // Spawn obstacles
  useEffect(() => {
    if (!gameStarted || gameOver || gameWon) return;

    const spawnObstacle = () => {
      if (Math.random() < OBSTACLE_SPAWN_RATE) {
        const orbit = Math.floor(Math.random() * ORBIT_COUNT);
        const newObstacle = {
          id: Date.now() + Math.random(),
          orbit,
          y: -OBSTACLE_SIZE,
        };
        setObstacles((prev) => [...prev, newObstacle]);
      }
    };

    const obstacleInterval = setInterval(spawnObstacle, 100);
    return () => clearInterval(obstacleInterval);
  }, [gameStarted, gameOver, gameWon]);

  // Game loop
  useEffect(() => {
    if (!gameStarted || gameOver || gameWon) return;

    const gameLoop = setInterval(() => {
      // Move obstacles
      setObstacles((prevObstacles) => {
        const newObstacles = prevObstacles
          .map((obstacle) => ({
            ...obstacle,
            y: obstacle.y + OBSTACLE_SPEED,
          }))
          .filter((obstacle) => obstacle.y < gameAreaRef.current?.offsetHeight);

        // Check collisions
        const player = playerRef.current;
        if (player) {
          newObstacles.forEach((obstacle) => {
            if (
              obstacle.orbit === currentOrbit &&
              obstacle.y + OBSTACLE_SIZE >
                gameAreaRef.current.offsetHeight -
                  ORBIT_SPACING * (currentOrbit + 1) &&
              obstacle.y <
                gameAreaRef.current.offsetHeight - ORBIT_SPACING * currentOrbit
            ) {
              setGameOver(true);
              handleApiResponse(score);
            }
          });
        }

        return newObstacles;
      });

      // Update depth and score
      setDepth((prev) => prev + DEPTH_INCREMENT);
      setScore((prev) => prev + Math.floor(DEPTH_INCREMENT * SCORE_MULTIPLIER));
    }, 16);

    return () => clearInterval(gameLoop);
  }, [gameStarted, gameOver, gameWon, currentOrbit, score]);

  // Handle API response
  const handleApiResponse = async (points) => {
    if (points <= 0) {
      if (gameWon) {
        setGameWon(true);
      } else {
        setGameOver(true);
      }
      return;
    }

    try {
      const apiData = {
        gamePoints: points,
        gameType: "jumpingorbits",
      };
      const res = await userGameRewards(apiData);

      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));

        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  // Add analytics logging
  useEffect(() => {
    logPageView("jumpingorbits", "jumpingorbits");
  }, []);

  // Handle play again
  const handlePlayAgain = () => {
    setGameOver(false);
    setGameWon(false);
    setScore(0);
    setDepth(0);
    setCurrentOrbit(2);
    setObstacles([]);
    setGameStarted(false);
  };

  // Toggle menu
  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  return (
    <div
      ref={gameBoardRef}
      className={styles.container}
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        margin: "0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "5px", width: "100%" }}>
        <img
          onClick={toogleMenu}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          className="cancel-full"
          style={{ cursor: "pointer" }}
        />
        <h3
          className={styles.welcome_text}
          style={{ fontSize: "1.2em", margin: "0 0 5px 0" }}
        >
          JUMPING ORBITS
        </h3>

        <div
          className={styles.infoGrid}
          style={{
            fontSize: "0.9em",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            textAlign: "center",
          }}
        >
          <div className={styles.infoCol}>
            <span className={styles.infoLabel}>
              Depth: {Math.floor(depth)}m
            </span>
          </div>
          <div className={styles.infoCol}>
            <span className={styles.infoLabel}>Score: {score}</span>
          </div>
        </div>
      </div>

      <div
        className={styles.gameArea}
        ref={gameAreaRef}
        style={{
          flex: 1,
          width: "100%",
          position: "relative",
          overflow: "hidden",
          margin: "0",
          touchAction: "none",
          maxWidth: "100%",
        }}
        onTouchStart={handleTouchStart}
      >
        {!gameStarted ? (
          <div className={styles.startScreen}>
            <button
              className={styles.control_btn}
              onClick={initializeGame}
              onTouchEnd={initializeGame}
            >
              Start Game
            </button>
          </div>
        ) : (
          <>
            {/* Orbits */}
            {Array.from({ length: ORBIT_COUNT }).map((_, index) => (
              <div
                key={index}
                className={styles.orbit}
                style={{
                  bottom: `${index * ORBIT_SPACING}px`,
                  backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)`,
                }}
              />
            ))}

            {/* Player */}
            <div
              ref={playerRef}
              className={styles.player}
              style={{
                bottom: `${currentOrbit * ORBIT_SPACING + PLAYER_SIZE / 2}px`,
              }}
            >
              <div className={styles.playerInner} />
            </div>

            {/* Obstacles */}
            {obstacles.map((obstacle) => (
              <div
                key={obstacle.id}
                className={styles.obstacle}
                style={{
                  bottom: `${obstacle.y}px`,
                  left: `${
                    obstacle.orbit * ORBIT_SPACING +
                    (ORBIT_SPACING - OBSTACLE_SIZE) / 2
                  }px`,
                }}
              />
            ))}

            {/* Mobile controls */}
            {showMobileControls && (
              <div className={styles.mobileControls}>
                <div className={styles.mobileInstructions}>
                  <p>Tap left/right to jump orbits</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Game Over Screen */}
      {gameOver && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className={styles.quento_epic}>Game Over!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={handlePlayAgain}
                onTouchEnd={handlePlayAgain}
              />
              <div className={styles.quento_epic_div}>
                <p className={styles.quento_pointsLabel}>SCORE</p>
                <div className={styles.quento_pointsValue1}>
                  <img
                    src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                    alt="coin"
                  />
                  <p className={styles.quento_pointsNumber}>{score}</p>
                </div>
              </div>
              <p className={styles.quento_bottom_text}>
                Keep playing! Challenge yourself to go deeper!
              </p>
              <button
                className={styles.control_btn}
                onClick={handlePlayAgain}
                onTouchEnd={handlePlayAgain}
              >
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions Button */}
      <p
        className={styles.quest}
        onClick={() => setShowInstructions(true)}
        onTouchEnd={() => setShowInstructions(true)}
        title="How to Play"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
        </svg>
      </p>

      {/* Instructions Screen */}
      {showInstructions && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className={styles.quento_epic}>How to Play</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={() => setShowInstructions(false)}
                onTouchEnd={() => setShowInstructions(false)}
              />
              <div className={styles.quento_epic_div}>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>⬅️ ➡️</div>
                  <p className={styles.instruction_text}>
                    Tap left/right to jump between orbits
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>⚠️</div>
                  <p className={styles.instruction_text}>
                    Avoid the red obstacles
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>⬇️</div>
                  <p className={styles.instruction_text}>
                    Travel deeper to score more points
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>🎯</div>
                  <p className={styles.instruction_text}>
                    Score increases with depth
                  </p>
                </div>
              </div>
              <button
                className={styles.control_btn}
                onClick={() => setShowInstructions(false)}
                onTouchEnd={() => setShowInstructions(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JumpingOrbits;
